import React, { useCallback, useMemo, useRef, useState } from 'react';
import { gql, useMutation } from '@apollo/react-hooks';
import type {
  ActionFn,
  Action,
  DataGridProps,
  SetIsLoading,
  ActionEvent,
  Filter,
} from '#/common/components/DataGrid';
import useToast from '#/common/hooks/useToast';
import useSession from '#/common/hooks/useSession';
import DataGrid from '#/common/components/DataGrid';
import { getAssetList } from '#/graphql/queries';
import { linkAssets } from '#/graphql/mutations';
import useAssetDelete from '../../hooks/useDeleteAssetMutation';
import useAssetPlanScopeSummaryLazyQuery from '../../hooks/useAssetPlanScopeSummaryLazyQuery';
import { createAssetColumns } from './columns';
import { actions } from './actions';
import { Asset, LinkAssetsMutation, LinkAssetsMutationVariables } from '#/graphql/types';
import { UserRole } from '#/common/types/userTypes';
import CreateAssetPlanForm, {
  AssetPlanScope,
} from '#/modules/asset/components/AssetDataGrid/CreateAssetPlanForm';
import MatchAssetsModal from '#/modules/asset/components/MatchAssetsModal';

const getAssetListQuery = gql(getAssetList);

export type TenantSummary = {
  id: string;
  name?: string;
};

const userSelectedActionsPerRole: Record<UserRole, string[]> = {
  [UserRole.asset_manager]: ['delete-assets'],
  [UserRole.trane_admin]: ['delete-assets'],
  [UserRole.technician]: [],
};

const gridActionsPerRole: Record<UserRole, string[]> = {
  [UserRole.asset_manager]: ['new'],
  [UserRole.trane_admin]: ['new'],
  [UserRole.technician]: [],
};

const gridOptionsPerRole: Record<UserRole, Partial<DataGridProps<Asset>>> = {
  [UserRole.asset_manager]: { selectionMode: 'multiple' },
  [UserRole.trane_admin]: { selectionMode: 'multiple' },
  [UserRole.technician]: { selectionMode: 'none' },
};

interface AssetDataGridProps {
  permanentFilters?: Filter[];
}

const AssetDataGrid: React.FC<AssetDataGridProps> = ({ permanentFilters }) => {
  const { confirm, alert } = useToast();
  const { user } = useSession();
  const { deleteAsset } = useAssetDelete();
  const fetchAssetPlanScopeSummary = useAssetPlanScopeSummaryLazyQuery();
  const [linkAssetsMutation] = useMutation<LinkAssetsMutation, LinkAssetsMutationVariables>(
    gql(linkAssets),
  );
  const assetsGridRef = useRef<any>(null);
  const [isCreatePlanModalVisible, setCreatePlanModalVisible] = useState(false);
  const [isPreparingCreateAssetPlan, setIsPreparingCreateAssetPlan] = useState(false);
  const [isMatchItemsModalVisible, setMatchItemsModalVisible] = useState(false);
  const [isMatchingAssets, setIsMatchingAssets] = useState(false);
  const [assetToMatch, setAssetToMatch] = useState<Asset | undefined>();

  const [planScope, setPlanScope] = useState<AssetPlanScope>({
    includedAssetCount: 0,
    selectedAssetIds: [],
    invalidStatusCount: 0,
  });

  const role = user?.role as UserRole | undefined;

  const deleteItem = useCallback(
    async (ids: string[], { setIsLoading }: { setIsLoading?: SetIsLoading }) => {
      if (!ids) {
        return undefined;
      }
      const sure = await confirm('error', `Are you sure you want to delete these assets?`);
      if (!sure) {
        return;
      }
      setIsLoading?.(true, 'Deleting data...');
      await deleteAsset(ids.join(','));
      setIsLoading?.(false);
    },
    [deleteAsset, confirm],
  );

  const userSelectedActions: ActionFn<Asset>[] = useMemo(() => {
    const res: ActionFn<Asset>[] = [];

    if (role && userSelectedActionsPerRole[role]?.includes('delete-assets')) {
      res.push({
        key: 'delete-assets',
        icon: 'trash',
        text: 'Delete',
        onClick: async (e) => {
          await deleteItem(
            e.selectedKeys.map((i: any) => i.id || i),
            { setIsLoading: e.setIsLoading },
          );
          e.dg?.instance.clearSelection();
          e.dg?.instance.refresh();
        },
      });
    }

    return res;
  }, [role, deleteItem]);

  const closeCreateAssetPlanModal = useCallback(() => {
    setCreatePlanModalVisible(false);
  }, []);

  const openMatchItemsModal = useCallback((asset: Asset) => {
    setIsMatchingAssets(false);
    setAssetToMatch(asset);
    setMatchItemsModalVisible(true);
  }, []);

  const closeMatchItemsModal = useCallback(() => {
    setIsMatchingAssets(false);
    setMatchItemsModalVisible(false);
    setAssetToMatch(undefined);
  }, []);

  const onConfirmMatchAsset = useCallback(
    async (selectedTisAssetId: string | null) => {
      if (!assetToMatch?.id) {
        await alert('error', 'No FSM asset selected for matching.');
        return;
      }

      try {
        setIsMatchingAssets(true);

        const response = await linkAssetsMutation({
          variables: {
            tisAssetId: selectedTisAssetId ?? null,
            fsmAssetId: assetToMatch.id,
          },
        });

        const result = response.data?.linkAssets;
        if (result?.status !== 200) {
          throw new Error(result?.errors?.join('\n') || 'Failed to match assets');
        }

        assetsGridRef.current?.instance?.refresh?.();
        closeMatchItemsModal();

        await alert(
          'success',
          selectedTisAssetId
            ? 'Asset matched successfully.'
            : 'Asset matching cleared successfully.',
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to match assets';
        setIsMatchingAssets(false);
        await alert('error', message);
      } finally {
        setIsMatchingAssets(false);
      }
    },
    [alert, assetToMatch, closeMatchItemsModal, linkAssetsMutation],
  );

  const onCreateAssetPlan = useCallback(
    async (e: ActionEvent<Asset>) => {
      const combinedFilter = e.dg?.instance?.getCombinedFilter?.();
      const activeFilters = Array.isArray(combinedFilter) ? combinedFilter : [];
      const hasSelection = !!e.selectedItems?.length;
      const selectedItems = e.selectedItems || [];

      const nextSelectedAssetIds = selectedItems.map((asset) => asset.id);
      const nextScopeCondition =
        hasSelection || activeFilters.length === 0 ? undefined : JSON.stringify(activeFilters);
      const tenantFilter = hasSelection
        ? JSON.stringify(['id', 'isoneof', nextSelectedAssetIds])
        : nextScopeCondition;

      try {
        setIsPreparingCreateAssetPlan(true);

        const summary = await fetchAssetPlanScopeSummary({
          variables: {
            params: {
              filter: tenantFilter,
            },
          },
        });

        const tenants = (summary?.tenants as TenantSummary[] | undefined) ?? [];
        const invalidStatusCount = summary?.invalidStatusCount ?? 0;
        const validStatusCount = summary?.validStatusCount ?? 0;

        if (tenants.length > 1) {
          alert(
            'error',
            `Selected assets span multiple organizations. Please narrow your selection to one organization.`,
          );
          return;
        }
        if (invalidStatusCount > 0 && validStatusCount === 0) {
          alert(
            'error',
            `All selected assets have invalid statuses. Please adjust your selection to include assets that are in-stock or installed.`,
          );
          return;
        }
        setPlanScope({
          includedAssetCount: validStatusCount,
          selectedAssetIds: nextSelectedAssetIds,
          selectedTenantSummary: tenants[0],
          scopeCondition: nextScopeCondition,
          invalidStatusCount,
        });
        setCreatePlanModalVisible(true);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'Unable to prepare asset plan creation at this time.';
        await alert('error', message);
      } finally {
        setIsPreparingCreateAssetPlan(false);
      }
    },
    [alert, fetchAssetPlanScopeSummary],
  );

  const gridActions = useMemo<Action<Asset>[]>(() => {
    const baseActions = actions
      .filter((a) => typeof a !== 'object' || (role && gridActionsPerRole[role]?.includes(a?.key)))
      .map((a) => {
        if (typeof a === 'string') {
          return a;
        }
        const result = { ...a };
        return result;
      })
      .filter((a) => !!a);

    if (role !== UserRole.asset_manager && role !== UserRole.trane_admin) {
      return baseActions;
    }

    return [
      ...baseActions,
      {
        key: 'create-asset-plan',
        icon: 'plus',
        text: '+ Asset Plan',
        showTextOnly: true,
        type: 'special',
        disabled: (e) => {
          const combinedFilter = e.dg?.instance?.getCombinedFilter?.();
          const hasActiveFilters = combinedFilter !== undefined;
          const totalCount = e.dg?.instance?.totalCount?.() || 0;
          const hasSelection = !!e.selectedItems?.length;
          return (
            isPreparingCreateAssetPlan || !(hasSelection || (hasActiveFilters && totalCount > 0))
          );
        },
        onClick: (e) => {
          onCreateAssetPlan(e);
        },
      },
    ];
  }, [role, isPreparingCreateAssetPlan, onCreateAssetPlan]);

  const columns = useMemo(
    () =>
      createAssetColumns({
        onEditMatch: openMatchItemsModal,
        isTechnician: role === UserRole.technician,
      }),
    [openMatchItemsModal, role],
  );

  if (!role) return null;

  return (
    <>
      <DataGrid
        id="assets"
        pluralText="Assets"
        singularText="Asset"
        columns={columns}
        recordUrl="/assets/:id"
        actions={gridActions}
        selectedActions={userSelectedActions}
        getItemsQuery={getAssetListQuery}
        permanentFilters={permanentFilters}
        setRef={(ref) => {
          assetsGridRef.current = ref;
        }}
        {...gridOptionsPerRole[role]}
      />

      <CreateAssetPlanForm
        visible={isCreatePlanModalVisible}
        scope={planScope}
        onClose={closeCreateAssetPlanModal}
      />

      {isMatchItemsModalVisible && (
        <MatchAssetsModal
          visible={isMatchItemsModalVisible}
          asset={assetToMatch}
          isSubmitting={isMatchingAssets}
          onClose={closeMatchItemsModal}
          onConfirm={onConfirmMatchAsset}
        />
      )}
    </>
  );
};

export default React.memo(AssetDataGrid);
